import React, { useState } from 'react';
import { Eye, EyeOff, UserPlus, Mail, Lock, User } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { SecurityMessage, RegistrationSecurityStatus } from './SecurityMessage';
import { useToast } from '@/contexts/ToastContext';

interface RegisterFormProps {
  onSuccess?: () => void;
  onSwitchToLogin?: () => void;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({ 
  onSuccess, 
  onSwitchToLogin 
}) => {
  const { showSuccess, showError } = useToast();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [invitationToken, setInvitationToken] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [securityMessage, setSecurityMessage] = useState<{
    type: 'warning' | 'error' | 'info';
    title: string;
    message: string;
    suggestions?: string[];
  } | null>(null);

  // Lista de dominios permitidos para validación en frontend
  const allowedEmailDomains = [
    'gmail.com',
    'hotmail.com', 'hotmail.co.uk', 'hotmail.fr', 'hotmail.de', 'hotmail.es',
    'outlook.com', 'outlook.co.uk', 'outlook.fr', 'outlook.de', 'outlook.es',
    'live.com', 'live.co.uk', 'live.fr', 'live.de', 'live.es',
    'yahoo.com', 'yahoo.co.uk', 'yahoo.fr', 'yahoo.de', 'yahoo.es',
    'yahoo.com.mx', 'yahoo.com.ar', 'yahoo.com.br'
  ];

  const { register } = useAuth();

  const validateForm = (): string | null => {
    if (!name.trim()) {
      return 'El nombre es requerido';
    }

    if (!email.trim()) {
      return 'El correo electrónico es requerido';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'El formato del correo electrónico no es válido';
    }

    // Check if email domain is allowed
    const emailDomain = email.split('@')[1]?.toLowerCase();
    if (emailDomain && !allowedEmailDomains.includes(emailDomain)) {
      return 'Solo se permiten emails de Gmail, Hotmail, Yahoo y Outlook';
    }

    if (!password) {
      return 'La contraseña es requerida';
    }

    if (password.length < 6) {
      return 'La contraseña debe tener al menos 6 caracteres';
    }

    if (password !== confirmPassword) {
      return 'Las contraseñas no coinciden';
    }

    // Validate invitation token ONLY if provided
    if (invitationToken.trim() && invitationToken.length !== 64) {
      return 'El token de invitación debe tener 64 caracteres';
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      showError(validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('🚀 [REGISTER] Starting registration process...');
      
      const result = await register({ 
        name: name.trim(), 
        email: email.trim().toLowerCase(), 
        password,
        ...(invitationToken.trim() && { invitationToken: invitationToken.trim() })
      });
      
      console.log('📝 [REGISTER] Registration result:', result);
      
      if (result.success) {
        console.log('✅ [REGISTER] Success - calling onSuccess');
        showSuccess('¡Cuenta creada exitosamente!');
        onSuccess?.();
      } else {
        console.log('❌ [REGISTER] Failed - showing error message:', result.message);
        
        // Check if it's a security-related error
        if (result.message?.includes('políticas de seguridad') || result.message?.includes('bloqueado') || result.message?.includes('detectado')) {
          setSecurityMessage({
            type: 'error',
            title: 'Registro no permitido',
            message: result.message || 'Nuestros sistemas han detectado múltiples intentos de registro desde tu ubicación. No se permiten más registros gratuitos.',
            suggestions: ['Si ya tienes una cuenta, inicia sesión', 'Si necesitas ayuda, contacta soporte']
          });
        } else {
          showError(result.message || 'Error al crear la cuenta');
        }
        // NO llamar onSuccess ni onSwitchToLogin aquí
      }
    } catch (error) {
      console.error('💥 [REGISTER] Caught error:', error);
      showError('Error de conexión');
      // NO llamar onSuccess ni onSwitchToLogin aquí
    } finally {
      console.log('🏁 [REGISTER] Finishing registration process');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-stone-900 rounded-lg shadow-lg border border-stone-700 p-8">
        <div className="text-center mb-8">
          <div className="mx-auto w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center mb-4">
            <UserPlus className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-stone-100">Crear Cuenta Gratuita</h2>
          <p className="text-stone-400 mt-2">Únete a Ads Finder y descubre anuncios ganadores con nuestro plan gratuito</p>
        </div>

        {/* Security Status */}
        <RegistrationSecurityStatus 
          isChecking={isSubmitting}
          securityMessage={securityMessage || undefined}
        />

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-stone-300 mb-2">
              Nombre Completo
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-stone-500" />
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-stone-600 rounded-lg bg-stone-950 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-stone-100 placeholder-stone-500"
                placeholder="Tu nombre completo"
                required
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-stone-300 mb-2">
              Correo Electrónico
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-stone-500" />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-stone-600 rounded-lg bg-stone-950 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-stone-100 placeholder-stone-500"
                placeholder="tu@email.com"
                required
                disabled={isSubmitting}
              />
            </div>
            <p className="text-xs text-stone-500 mt-1">
              Solo se permiten emails de Gmail, Hotmail, Yahoo y Outlook
            </p>
          </div>

          <div>
            <label htmlFor="invitationToken" className="block text-sm font-medium text-stone-300 mb-2">
              Token de Invitación <span className="text-stone-500">(Opcional)</span>
            </label>
            <div className="relative">
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-stone-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m0 0a2 2 0 012 2m-2-2h-6m6 0v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9a2 2 0 012-2h2M7 7a2 2 0 012-2h6a2 2 0 012 2v2a2 2 0 01-2 2H9a2 2 0 01-2-2V7z" />
              </svg>
              <input
                id="invitationToken"
                type="text"
                value={invitationToken}
                onChange={(e) => setInvitationToken(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-stone-600 rounded-lg bg-stone-950 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-stone-100 placeholder-stone-500 font-mono text-sm"
                placeholder="Pega aquí tu código de invitación (opcional)"
                disabled={isSubmitting}
                maxLength={64}
              />
            </div>
            <p className="text-xs text-stone-500 mt-1">
              Si tienes un código de invitación para planes premium, ingrésalo aquí. Si no, puedes registrarte gratis con el plan básico.
            </p>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-stone-300 mb-2">
              Contraseña
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-stone-500" />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-3 border border-stone-600 rounded-lg bg-stone-950 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-stone-100 placeholder-stone-500"
                placeholder="••••••••"
                required
                disabled={isSubmitting}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-stone-500 hover:text-stone-300"
                disabled={isSubmitting}
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
            <p className="text-xs text-stone-500 mt-1">
              Mínimo 6 caracteres
            </p>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-stone-300 mb-2">
              Confirmar Contraseña
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-stone-500" />
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-3 border border-stone-600 rounded-lg bg-stone-950 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-stone-100 placeholder-stone-500"
                placeholder="••••••••"
                required
                disabled={isSubmitting}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-stone-500 hover:text-stone-300"
                disabled={isSubmitting}
              >
                {showConfirmPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-stone-100 text-stone-900 py-3 px-4 rounded-full font-semibold hover:bg-stone-200 focus:ring-2 focus:ring-secondary-400 focus:ring-offset-2 focus:ring-offset-stone-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-gum border border-stone-300"
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-stone-900 border-t-transparent rounded-full animate-spin" />
                <span>Creando cuenta...</span>
              </>
            ) : (
              <>
                <UserPlus className="w-5 h-5" />
                <span>Crear Cuenta</span>
              </>
            )}
          </button>
        </form>

        {onSwitchToLogin && (
          <div className="mt-6 text-center">
            <p className="text-stone-400">
              ¿Ya tienes una cuenta?{' '}
              <button
                onClick={onSwitchToLogin}
                className="text-emerald-400 hover:text-emerald-300 font-medium"
                disabled={isSubmitting}
              >
                Inicia sesión aquí
              </button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
